import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { HistoricalWeather } from '/@/types/weather/forecast';
import { formatDate, formatDateCMProgressBar } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import Title from '/@/v3/components/title';

const dateFormatter = (date) => {
    const formatedData = formatDate(new Date(date));
    return formatDateCMProgressBar(formatedData);
};

export const References = ({ references }: { references: { color: string; label: string; dashed?: boolean }[] }) => (
    <Grid item container sx={{ width: 'fit-content' }} spacing={1} p={1}>
        <Grid item>
            <Typography variant='caption' color='primary.mui.inactive'>
                {`${_('Referencias')}:`}
            </Typography>
        </Grid>
        {references.map(({ color, label, dashed }) => (
            <Grid key={label} item container alignItems='center' sx={{ gap: 1, width: 'fit-content' }}>
                <Grid item>
                    <Box
                        sx={{
                            height: 5,
                            width: 5,
                            backgroundColor: color,
                            borderRadius: '50%',
                            ...((dashed && {
                                position: 'relative',
                                borderRadius: 0,
                                height: 4,
                                width: 8,
                                '&::after': {
                                    content: "' '",
                                    backgroundColor: 'white!important',
                                    width: '33%',
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    right: '50%',
                                    transform: 'translateX(50%)',
                                },
                            }) ||
                                {}),
                        }}
                    />
                </Grid>
                <Grid item>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {label}
                    </Typography>
                </Grid>
            </Grid>
        ))}
    </Grid>
);

const GDUGraphic = ({
    historicalWeather,
    cropMonitoring,
    small,
    isDesktopSm,
    isMobile,
    expanded,
    setExpanded,
    isPdf = false,
}: {
    historicalWeather: HistoricalWeather;
    cropMonitoring: CropMonitoring;
    small?: boolean;
    isDesktopSm?: boolean;
    isMobile?: boolean;
    expanded?: boolean;
    setExpanded?: (expanded: boolean) => void;
    isPdf?: boolean;
}) => {
    const theme = settings.app.theme;
    const inactiveColor = colors.themes[theme].mui.primary.colors.inactive;
    const cropData = getCropMonitoringData(cropMonitoring);
    const dates = cropData?.stages.map((cm) => {
        return { date: cm.date, phenology: cm.vegetativeStage };
    });

    function getDatesInRange(startDate, endDate) {
        const date = new Date(startDate.getTime());

        while (date <= endDate) {
            dates.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }

        return dates;
    }

    const d1 = new Date(cropMonitoring.plantingDate);
    let d2 = Date.now();
    const maturityStage = cropData?.stages.find((stage) => stage.phenology === 'maturity');
    if (maturityStage) {
        d2 = new Date(maturityStage.date);
    }

    let data = getDatesInRange(d1, d2)
        .map((d) => {
            const date = new Date(d).setHours(0, 0, 0, 0);
            const gdus = historicalWeather?.results?.find((hw) => {
                return new Date(hw.date).setHours(0, 0, 0, 0) === date;
            });

            const phenology = cropData?.stages.find((s) => {
                return new Date(s.date).setHours(0, 0, 0, 0) === date;
            });

            return {
                name: moment(d).valueOf(),
                gdu: gdus && gdus.gduAccum,
                vegetativeStage: phenology,
            };
        })
        .filter((d) => {
            return Boolean(d.name);
        });

    data = data.map((d, i) => {
        if (i === 0) {
            d.vegetativeStage = 0;
        }
        if (d.vegetativeStage) {
            if (d.vegetativeStage.phenology === 'flowering') {
                d.vegetativeStage = 'R1';
            } else if (d.vegetativeStage.phenology === 'maturity') {
                d.vegetativeStage = 'MF';
            } else {
                d.vegetativeStage = d.vegetativeStage.vegetativeStage;
            }
        }
        return d;
    });

    data = data.map((d, i) => {
        if (i === 0) {
            d.phenology = 0;
        }
        if (d.vegetativeStage || i === 0) {
            d.phenology = d.vegetativeStage;
        } else {
            d.phenology = data[i - 1].phenology;
        }
        return d;
    });

    const width = small ? (isDesktopSm ? 400 : 500) : '100%';

    const height = small ? 280 : isMobile ? 250 : 350;

    const renderChart = () => {
        const chartContent = (
            <>
                <CartesianGrid strokeDasharray='1 1' vertical={false} />
                <XAxis
                    axisLine={false}
                    tickLine={false}
                    dataKey='name'
                    tickFormatter={dateFormatter}
                    scale='utc'
                    type='number'
                    interval={
                        small || isMobile
                            ? Math.round(data.length / 5)
                            : isDesktopSm
                              ? Math.round(data.length / 10)
                              : Math.round(data.length / 20)
                    }
                    domain={[
                        moment(d1.toISOString().slice(0, 10)).valueOf(),
                        moment(new Date(d2).toISOString().slice(0, 10)).valueOf(),
                    ]}
                    style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        paddingTop: 5,
                        color: inactiveColor,
                    }}
                />
                <YAxis
                    tickLine={false}
                    axisLine={false}
                    yAxisId='left'
                    dataKey='gdu'
                    style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: inactiveColor,
                    }}
                    label={{
                        value: 'GDU',
                        position: 'left',
                        angle: -90,
                        offset: -15,
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: inactiveColor,
                        },
                    }}
                />

                <YAxis
                    yAxisId='right'
                    axisLine={false}
                    tickLine={false}
                    orientation='right'
                    dataKey='vegetativeStage'
                    domain={[0, 13]}
                    style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: inactiveColor,
                    }}
                    type='category'
                    label={{
                        value: _('Estado Vegetativo'),
                        position: 'right',
                        angle: 90,
                        offset: -15,
                        dy: -30,
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: inactiveColor,
                        },
                    }}
                    tickFormatter={(el) => {
                        if (typeof el === 'number') {
                            return `V${el}`;
                        }
                        return el;
                    }}
                />
                {!isPdf && <Tooltip payload={data} content={<CustomTooltip />} />}
                <Line
                    yAxisId='left'
                    type='monotone'
                    dataKey='gdu'
                    stroke='#00BCFF'
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 5 }}
                />
                <Line
                    yAxisId='right'
                    type='monotone'
                    dataKey='vegetativeStage'
                    connectNulls
                    dot={false}
                    stroke='#89D329'
                    activeDot={{ r: 6, strokeWidth: 5 }}
                />
            </>
        );
        if (isPdf) {
            return (
                <Box
                    sx={{
                        '& svg': {
                            overflow: 'visible',
                        },
                    }}
                >
                    <LineChart
                        width={1000}
                        height={400}
                        data={data}
                        margin={{
                            top: 15,
                            bottom: 0,
                            right: isMobile ? 0 : 30,
                        }}
                    >
                        {chartContent}
                    </LineChart>
                </Box>
            );
        }
        return (
            <ResponsiveContainer width={width} height={height}>
                <LineChart
                    data={data}
                    margin={{
                        top: 15,
                        bottom: 0,
                        right: isMobile ? 0 : 30,
                    }}
                >
                    {chartContent}
                </LineChart>
            </ResponsiveContainer>
        );
    };

    return renderChart();
};

export default GDUGraphic;

const CustomTooltip = ({ payload }) => {
    const formattedData = () => {
        if (payload.length) {
            if (typeof payload?.[0]?.payload.phenology === 'number') {
                return `V${payload?.[0]?.payload.phenology}`;
            }
            return payload?.[0]?.payload.phenology;
        }
    };
    return (
        <Grid
            container
            sx={{
                borderRadius: 2,
                border: '0.5px solid #E1E1E1',
                p: 1,
                background: 'white',
            }}
        >
            <Grid item xs={12} sx={{ borderBottom: '0.5px solid #E1E1E1', py: 1 }}>
                <Grid container alignItems='center' flexDirection='row' sx={{ gap: 1 }}>
                    <Grid item>
                        <span style={{ fontSize: '14px' }} className='material-icons'>
                            calendar_month
                        </span>
                    </Grid>
                    <Grid item>
                        <Title size='sm' bolder>
                            {Boolean(payload.length) && dateFormatter(payload?.[0]?.payload.name)}
                        </Title>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    <Grid item container xs={12} sx={{ pt: 1 }} direction='row' alignItems='center' spacing={1}>
                        <Grid item>
                            <div style={{ height: 5, width: 5, backgroundColor: '#00BCFF', borderRadius: '50%' }} />
                        </Grid>
                        <Grid item>
                            <Title size='sm' bolder>
                                {_('GDU:')}
                            </Title>
                        </Grid>
                        <Grid item>
                            <div style={{ color: '#00BCFF', fontWeight: 600 }}>
                                {Boolean(payload.length) && payload?.[0]?.value}
                            </div>
                        </Grid>
                    </Grid>
                    <Grid item container xs={12} sx={{ pt: 1 }} direction='row' alignItems='center' spacing={1}>
                        <Grid item>
                            <div style={{ height: 5, width: 5, backgroundColor: '#89D329', borderRadius: '50%' }} />
                        </Grid>
                        <Grid item>
                            <Title size='sm' bolder>
                                {_('EV:')}
                            </Title>
                        </Grid>
                        <Grid item>
                            <div style={{ color: '#89D329', fontWeight: 600 }}>{formattedData()}</div>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
